import { Outlet } from '@tanstack/react-router';
import { ResultsLayout } from '@/layouts';
import { useSearchContext } from '@/context';
import {
  Heading,
  SearchBar,
  Filter,
  QuickLinks,
  QuickLink,
  FilterDrawer,
  DidYouMeanPanel,
  ConditionalResourceCard,
  getAllResourceConfigs,
} from '@/components';
import { useFilterStore } from '@/store';
import { useDeviceType } from '@/hooks';
import {
  allQuickLinks,
  coursesDomesticQuickLinks,
  courseInternationalQuickLinks,
  eventsQuickLinks,
  newsQuickLinks,
  peopleQuickLinks,
  researchQuickLinks,
} from '@/data';

export const Results = () => {
  const { currentFilterSet, region } = useFilterStore();
  const { facet, searchQuery, searchResults } = useSearchContext();
  const { isDesktop } = useDeviceType();

  const sanitisedSearchTerms = searchQuery && searchQuery.trim().toLowerCase();

  const getQuickLinks = () => {
    switch (facet) {
      case 'all':
        return allQuickLinks;
      case 'courses':
        return region === 'domestic' ? coursesDomesticQuickLinks : courseInternationalQuickLinks;
      case 'events':
        return eventsQuickLinks;
      case 'news':
        return newsQuickLinks;
      case 'people':
        return peopleQuickLinks;
      case 'research':
        return researchQuickLinks;
      default:
        return allQuickLinks;
    }
  };

  const quickLinks = getQuickLinks();

  return (
    <ResultsLayout
      mastheadSlot={
        <div className="flex w-full items-center justify-center bg-deep-blue-50 p-16 px-4 pb-0 pt-8 md:px-6 lg:rounded-2xl lg:pt-16">
          <div className="relative flex w-full max-w-[920px] flex-col lg:px-10">
            <Heading variant="h1" className="block w-full text-center leading-[125%]">
              Explore Curtin University
            </Heading>
            <div className={searchResults?.didYouMean ? 'mt-8' : 'mb-8 mt-8'}>
              <SearchBar />
            </div>
            {searchResults?.didYouMean && (
              <div className="mb-8 mt-3 w-full text-left">
                <DidYouMeanPanel />
              </div>
            )}
            <div className="h-16">{/** (Space for the search tabs when not stuck on the top) */}</div>
          </div>
        </div>
      }
      leftSideBarSlot={<Filter filterData={currentFilterSet} />}
      mainContentSlot={
        <div className="size-full">
          <Outlet />
          {!isDesktop && <FilterDrawer facet={facet} filterData={currentFilterSet} />}
        </div>
      }
      rightSideBarSlot={
        <div className="mt-3 w-full">
          <QuickLinks title="Go to...">
            {quickLinks.map((link) => (
              <QuickLink key={link.label} to={link.to} label={link.label} showIcon={link.showIcon} />
            ))}
          </QuickLinks>
          {getAllResourceConfigs().map((config) => {
            const Icon = config.icon;
            return (
              <ConditionalResourceCard
                key={config.id}
                title={config.title}
                description={config.description}
                ctaLabel={config.ctaLabel}
                href={config.href}
                icon={<Icon aria-hidden="true" />}
                searchTerms={config.searchTerms}
                facets={config.facets}
                currentSearchTerm={sanitisedSearchTerms}
                currentFacet={facet}
              />
            );
          })}
        </div>
      }
    />
  );
};
